// src/pages/TodoList.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import Avatar from "../components/Avatar";
import socket from "../socket";

// Auto-resize textarea component
function AutoResizeTextarea({ value, onChange, placeholder, className }) {
  const ref = useRef(null);
  const handle = (e) => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
    onChange && onChange(e);
  };
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      className={className}
      rows={1}
      placeholder={placeholder}
      value={value}
      onChange={handle}
      style={{ resize: "none", overflow: "hidden" }}
    />
  );
}

function formatDueLabel(dueDate, someday) {
  if (someday) return "Vid tillfälle";
  if (!dueDate) return "";
  const d = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays === 0) return "Idag";
  if (diffDays === 1) return "Imorgon";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}-${yyyy}`;
}

export default function TodoList() {
  const { user, fetchAssignableUsers } = useAuth();
  const [todos, setTodos] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    text: "",
    phone: "",
    dueDate: "",
    someday: false,
    assignedToUserId: ""
  });

  // completed separat
  const [completed, setCompleted] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [selectedTodo, setSelectedTodo] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const fetchTodos = async () => {
    const { data } = await api.get("/todos");
    setTodos(data.todos);
  };

const fetchCompleted = async () => {
  const { data } = await api.get("/todos/completed?limit=25&skip=0");
  setCompleted(
    data.todos
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
      .slice(0, 25)
  );
};

  // init
  useEffect(() => {
    (async () => {
      await fetchTodos();
      setCompleted([]);
      setSkip(0);
      setHasMore(true);
      await fetchCompleted();
    })();
  }, []);

  // load assignable users
  const loadAssignableUsers = async (seed = []) => {
    try {
      const list = await fetchAssignableUsers();
      setAllUsers(list);
    } catch {
      const map = new Map();
      const add = (u) =>
        u &&
        map.set(u.id || u._id, {
          id: u.id || u._id,
          firstName: u.firstName,
          lastName: u.lastName,
          avatar: u.avatar
        });
      seed.forEach((t) => {
        add(t.createdBy);
        add(t.assignedTo);
        add(t.completedBy);
      });
      if (user) add(user);
      setAllUsers(Array.from(map.values()));
    }
  };

  useEffect(() => {
    socket.on("todo:created", (todo) =>
      setTodos(prev =>
        prev.some(t => t.id === todo.id) ? prev : [todo, ...prev]
      ),
    );

    socket.on("todo:updated", (todo) =>
      setTodos(prev => prev.map(t => (t.id === todo.id ? todo : t)))
    );

    socket.on("todo:completed", (todo) => {
      setTodos(prev =>
        prev.some(t => t.id === todo.id)
          ? prev.map(t => (t.id === todo.id ? todo : t))
          : [todo, ...prev]
      );

      setCompleted(prev => {
        if (todo.completed) {
          // Nytt slutfört → in högst upp, max 25 st
          const newList = [todo, ...prev.filter(t => t.id !== todo.id)];
          return newList
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
            .slice(0, 25); // max 25
        } else {
          // Ångra → ta bort från completed
          return prev.filter(t => t.id !== todo.id);
        }
      });
    });

    socket.on("todo:deleted", ({ id }) => {
      setTodos(prev => prev.filter(t => t.id !== id));
      setCompleted(prev => prev.filter(t => t.id !== id));
    });

    return () => {
      socket.off("todo:created");
      socket.off("todo:updated");
      socket.off("todo:completed");
      socket.off("todo:deleted");
    };
  }, []);

  useEffect(() => {
    if (allUsers.length === 0 && todos.length > 0) {
      loadAssignableUsers(todos);
    }
  }, [todos]); // eslint-disable-line

  // sortering
  const activeTodos = useMemo(() => todos.filter((t) => !t.completed), [todos]);

  const mineAssigned = useMemo(
    () =>
      activeTodos
        .filter((t) => t.assignedTo?.id === user?.id)
        .sort((a, b) => {
          const aKey =
            a.someday || !a.dueDate ? Infinity : new Date(a.dueDate).getTime();
          const bKey =
            b.someday || !b.dueDate ? Infinity : new Date(b.dueDate).getTime();
          return aKey - bKey;
        }),
    [activeTodos, user?.id]
  );

  const othersWithDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // början av dagen

    return activeTodos
      .filter((t) => t.assignedTo?.id !== user?.id && !t.someday && t.dueDate)
      .sort((a, b) => {
        const aCreated = new Date(a.createdAt);
        const bCreated = new Date(b.createdAt);

        // båda skapade idag -> sortera på dueDate
        if (aCreated >= today && bCreated >= today) {
          return new Date(a.dueDate) - new Date(b.dueDate);
        }

        // a idag, b inte idag -> a först
        if (aCreated >= today) return -1;
        if (bCreated >= today) return 1;

        // annars sortera på dueDate
        return new Date(a.dueDate) - new Date(b.dueDate);
      });
  }, [activeTodos, user?.id]);


  const othersNoDate = useMemo(
    () =>
      activeTodos.filter(
        (t) => t.assignedTo?.id !== user?.id && (t.someday || !t.dueDate)
      ),
    [activeTodos, user?.id]
  );

  // skapa
  const createTodo = async () => {
    if (!createForm.text.trim()) return alert("Text krävs");
    const payload = {
      text: createForm.text.trim(),
      phone: createForm.phone || "",
      someday: !!createForm.someday,
      dueDate: createForm.someday
        ? null
        : createForm.dueDate || new Date().toISOString().slice(0, 10),
      assignedToUserId: createForm.assignedToUserId || null
    };
    await api.post("/todos", payload);
    setCreateForm({
      text: "",
      phone: "",
      dueDate: "",
      someday: false,
      assignedToUserId: ""
    });
    setCreateOpen(false);
  };

  // edit
  const openEdit = (todo) => {
    setSelectedTodo(todo);
    setEditDraft({
      text: todo.text,
      phone: todo.phone || "",
      someday: !!todo.someday,
      dueDate: todo.dueDate ? todo.dueDate.slice(0, 10) : "",
      assignedToUserId: todo.assignedTo?.id || ""
    });
  };

  const saveEdit = async () => {
    if (!selectedTodo) return;
    await api.patch(`/todos/${selectedTodo.id}`, {
      text: editDraft.text,
      phone: editDraft.phone,
      someday: editDraft.someday,
      dueDate: editDraft.someday ? null : editDraft.dueDate || null,
      assignedToUserId: editDraft.assignedToUserId || null
    });
    setSelectedTodo(null);
    setEditDraft(null);
  };

  const completeToggle = async (todo) => {
    const msg = todo.completed ? "Ångra slutförd?" : "Markera som slutförd?";
    if (!window.confirm(msg)) return;
    await api.post(`/todos/${todo.id}/complete`);
  };

  const deleteTodo = async (id) => {
    if (!window.confirm("Är du säker på att du vill ta bort?")) return;
    await api.delete(`/todos/${id}`);
    setSelectedTodo(null);
  };

  // infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore) return;
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 200
      ) {
        fetchCompleted(skip);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [skip, hasMore]);

  // kort
  const TodoCard = ({ todo }) => (
    <div
      className="col-12"
      style={{ cursor: "pointer" }}
      onClick={() => openEdit(todo)}
    >
      <div className="card shadow-sm h-100 position-relative">
        <button
          className={`btn btn-sm btn-checkmark position-absolute bottom-0 end-0 m-2 ${
            todo.completed ? "btn-outline-secondary" : "btn-outline-success"
          }`}
          title={todo.completed ? "Ångra slutförd" : "Markera som slutförd"}
          onClick={(e) => {
            e.stopPropagation();
            completeToggle(todo);
          }}
        >
          {todo.completed ? "↺" : "✓"}
        </button>

        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <h5
              className={`card-title ${
                todo.completed ? "text-decoration-line-through text-muted" : ""
              }`}
            >
              {todo.text}
            </h5>
            <span className="badge bg-light text-dark border">
              {formatDueLabel(todo.dueDate, todo.someday)}
            </span>
          </div>

          <p
            className="card-text small text-muted d-flex align-items-center gap-2"
            style={{ minHeight: "24px" }}
          >
            {todo.assignedTo && (
              <>
              Tilldelad: 
              <span className="badge bg-light text-dark">
                <Avatar
                  initials={todo.assignedTo.avatar?.initials}
                  color={todo.assignedTo.avatar?.color}
                  size={18}
                />{" "}
                {todo.assignedTo.firstName}
              </span>
              </>
            )}
          </p>

          {todo.phone && (
            <p className="card-text small text-muted mb-0">Tel: {todo.phone}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Aktiva Jobb</h3>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
          Nytt jobb
        </button>
      </div>

      {mineAssigned.length > 0 && <h6 className="text-muted">Mina tilldelade</h6>}
      <div className="row g-3 mb-4">
        {mineAssigned.map((todo) => (
          <TodoCard key={todo.id} todo={todo} />
        ))}
      </div>

      {othersWithDate.length > 0 && <h6 className="text-muted">Kommande datum</h6>}
      <div className="row g-3 mb-4">
        {othersWithDate.map((todo) => (
          <TodoCard key={todo.id} todo={todo} />
        ))}
      </div>

      {othersNoDate.length > 0 && <h6 className="text-muted">Övriga</h6>}
      <div className="row g-3 mb-5">
        {othersNoDate.map((todo) => (
          <TodoCard key={todo.id} todo={todo} />
        ))}
        {mineAssigned.length + othersWithDate.length + othersNoDate.length ===
          0 && <p className="text-muted">Inga aktiva Jobb</p>}
      </div>

      <h3 className="mb-3">Slutförda Jobb</h3>
      <div className="row g-3">
        {completed.map((todo) => (
          <TodoCard key={todo.id} todo={todo} />
        ))}
        {completed.length === 0 && <p className="text-muted">Inga slutförda Jobb</p>}
      </div>
      {loadingMore && (
        <div className="text-center my-3">
          <div className="spinner-border text-secondary" />
        </div>
      )}

      {/* skapa-modal */}
      {createOpen && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Nytt jobb</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setCreateOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Text *</label>
                  <AutoResizeTextarea
                    className="form-control"
                    placeholder="Vad ska göras?"
                    value={createForm.text}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, text: e.target.value })
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Telefon (valfritt)</label>
                  <input
                    className="form-control"
                    value={createForm.phone}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, phone: e.target.value })
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label d-block">Tilldelad (valfritt)</label>
                  <select
                    className="form-select"
                    value={createForm.assignedToUserId}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        assignedToUserId: e.target.value
                      })
                    }
                  >
                    <option value="">Ej tilldelad</option>
                    {allUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="createSomeday"
                    checked={createForm.someday}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        someday: e.target.checked
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="createSomeday">
                    Vid tillfälle (ingen specifik dag)
                  </label>
                </div>

                {!createForm.someday && (
                  <div className="mb-3">
                    <label className="form-label">Datum för utförande</label>
                    <input
                      type="date"
                      className="form-control"
                      value={createForm.dueDate}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          dueDate: e.target.value
                        })
                      }
                    />
                    <div className="form-text">Lämnas tomt = idag</div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={createTodo}>
                  Spara
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setCreateOpen(false)}
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* redigera-modal */}
      {selectedTodo && editDraft && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Jobb-detaljer</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setSelectedTodo(null);
                    setEditDraft(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Text</label>
                  <AutoResizeTextarea
                    className="form-control"
                    value={editDraft.text}
                    onChange={(e) =>
                      setEditDraft({ ...editDraft, text: e.target.value })
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Telefon</label>
                  <input
                    className="form-control"
                    value={editDraft.phone}
                    onChange={(e) =>
                      setEditDraft({ ...editDraft, phone: e.target.value })
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label d-block">Tilldelad</label>
                  <select
                    className="form-select"
                    value={editDraft.assignedToUserId}
                    onChange={(e) =>
                      setEditDraft({
                        ...editDraft,
                        assignedToUserId: e.target.value
                      })
                    }
                  >
                    <option value="">Ej tilldelad</option>
                    {allUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="editSomeday"
                    checked={editDraft.someday}
                    onChange={(e) =>
                      setEditDraft({
                        ...editDraft,
                        someday: e.target.checked
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="editSomeday">
                    Vid tillfälle (ingen specifik dag)
                  </label>
                </div>

                {!editDraft.someday && (
                  <div className="mb-3">
                    <label className="form-label">Datum för utförande</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editDraft.dueDate}
                      onChange={(e) =>
                        setEditDraft({
                          ...editDraft,
                          dueDate: e.target.value
                        })
                      }
                    />
                  </div>
                )}

                <div className="small text-muted">
                  <div>
                    Skapad{" "}
                    {new Date(selectedTodo.createdAt).toLocaleString("sv-SE", {
                      hour: "2-digit",
                      minute: "2-digit",
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit"
                    })}{" "}
                    av {selectedTodo.createdBy?.firstName}{" "}
                    {selectedTodo.createdBy?.lastName}
                  </div>
                  {selectedTodo.completed && (
                    <div>
                      Slutförd{" "}
                      {new Date(selectedTodo.completedAt).toLocaleString(
                        "sv-SE",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit"
                        }
                      )}{" "}
                      av {selectedTodo.completedBy?.firstName}{" "}
                      {selectedTodo.completedBy?.lastName}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={saveEdit}>
                  Spara
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => deleteTodo(selectedTodo.id)}
                >
                  Ta bort
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setSelectedTodo(null);
                    setEditDraft(null);
                  }}
                >
                  Stäng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTodo &&
        !editDraft &&
        setEditDraft({
          text: selectedTodo.text,
          phone: selectedTodo.phone || "",
          someday: !!selectedTodo.someday,
          dueDate: selectedTodo.dueDate
            ? selectedTodo.dueDate.slice(0, 10)
            : "",
          assignedToUserId: selectedTodo.assignedTo?.id || ""
        })}
    </div>
  );
}
